/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Service from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import Guideline, { type GuidelineCategory } from 'embercom/models/ai-agent/guidelines';
import { tracked } from '@glimmer/tracking';
import type Settings from 'embercom/models/ai-agent/tone-of-voice-settings';
import type Store from '@ember-data/store';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import { BlocksDocument } from '@intercom/composer-ember';
import type EscalationPredicates from 'embercom/models/ai-agent/escalation-predicates';

export interface GuidelineExample {
  title: string;
  prompt: string;
}

enum Stat {
  USED = 'used',
  RESOLVED = 'resolved',
  ROUTED = 'routed',
}

export type GuidelineCategoryForExamples = Exclude<GuidelineCategory, 'other' | 'copilot'>;
type MappedGuidelineCategory =
  | 'communication'
  | 'clarification'
  | 'routing'
  | 'content'
  | 'spam-detection';

export type PreviewSettingsMessageType =
  | 'preview-settings-updated'
  | 'preview-guidelines-updated'
  | 'preview-guidelines-saved';

export const PREVIEW_REFRESH_INTERVAL = 3000;

export default class AiAgentGuidelineService extends Service {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare attributeService: $TSFixMe;

  @tracked typingTimeout: ReturnType<typeof setTimeout> | null = null;
  @tracked openGuidelineId: string | null = null;
  @tracked chartDefinitionsHash: Record<string, Record<Stat, any>> = {};
  @tracked hasUnsavedEscalationChanges = false;
  @tracked escalationPredicatesRecord: EscalationPredicates | null = null;

  guidelines: Guideline[];
  composerRefreshHooks: (() => void)[] = [];
  personalityCustomizationSettings: Settings;

  constructor() {
    super(...arguments);
    this.guidelines = this.store.peekAll('ai-agent/guidelines') as any;
    this.personalityCustomizationSettings = this.store.peekRecord(
      'ai-agent/tone-of-voice-settings',
      this.appService.app.id,
    );
  }

  get escalationPredicateGroup() {
    return this.escalationPredicatesRecord?.predicateGroup;
  }

  attributeInfoResolver(): AttributeInfoResolver {
    return new AttributeInfoResolver({
      attributes: this.appService.app.allowedAttributes,
      conversationAttributes:
        this.attributeService.nonArchivedConversationCustomAttributesWithoutFile.filter(
          (attr: any) => !attr.get('isClassificationAttribute'),
        ),
      customObjectAttributes: this.attributeService.customObjectsAttributeDescriptors,
      includeAppAttributes: false,
      includeCustomObjectAttributes: false, // Don't use this to avoid unfiltered conversation attributes
    });
  }

  /**
   * Converts a GuidelineCategory enum value to a translation key format.
   * Delegates to the Guideline model's static method.
   */
  convertCategoryToTranslationKey(category: GuidelineCategory): string {
    return Guideline.categoryToTranslationKey(category);
  }

  serializedBlocksFromPrompt(prompt: string | undefined | null) {
    if (!prompt) {
      return [];
    }

    let blocksDoc = new BlocksDocument(
      prompt.split('\n').map((line) => ({ type: 'paragraph', text: line, class: 'no-margin' })),
    );

    return blocksDoc.blocks;
  }

  getEmptyStateGuidelinesForCategory(guidelineCategory: GuidelineCategoryForExamples) {
    return this.guidelineExamples(guidelineCategory)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
  }

  guidelineExamples(guidelineCategory: GuidelineCategory): GuidelineExample[] {
    if (guidelineCategory === 'other' || guidelineCategory === 'copilot') {
      return [];
    }

    let examplesCategoryMapping: Record<GuidelineCategoryForExamples, MappedGuidelineCategory> = {
      tone: 'communication',
      clarification: 'clarification',
      routing: 'routing',
      content: 'content',
      spam_detection: 'spam-detection',
      voice_routing: 'routing',
      voice_clarification: 'clarification',
      voice_tone: 'communication',
    };

    let category: MappedGuidelineCategory = examplesCategoryMapping[guidelineCategory];

    return category ? this.getExamplesForCategory(category) : [];
  }

  updateChartDefinitions(newGuideline: Guideline) {
    this.chartDefinitionsHash[newGuideline.id] = {
      [Stat.USED]: newGuideline.chartDefinitionForStat(Stat.USED),
      [Stat.RESOLVED]: newGuideline.chartDefinitionForStat(Stat.RESOLVED),
      [Stat.ROUTED]: newGuideline.chartDefinitionForStat(Stat.ROUTED),
    };
  }

  getExamplesForCategory(category: MappedGuidelineCategory): GuidelineExample[] {
    let numberOfExamples;
    if (category === 'clarification') {
      numberOfExamples = 11;
    } else if (category === 'communication') {
      numberOfExamples = 11;
    } else if (category === 'content') {
      numberOfExamples = 5;
    } else if (category === 'spam-detection') {
      numberOfExamples = 1;
    } else {
      numberOfExamples = 10;
    }

    return Array.from({ length: numberOfExamples }, (_, i) => {
      let exampleNumber = i + 1;
      return {
        title: this.intl.t(`ai-agent.guidance.examples.${category}.example-${exampleNumber}.title`),
        prompt: this.intl.t(
          `ai-agent.guidance.examples.${category}.example-${exampleNumber}.prompt`,
        ),
      };
    });
  }

  callComposerRefreshHooks() {
    this.composerRefreshHooks.forEach((hook) => hook());
  }

  async handleAddFromExampleCategoryEmptyState(
    category: GuidelineCategory,
    example: GuidelineExample,
  ): Promise<Guideline> {
    let newGuideline = await this.createNewGuideline(category, example);

    this.updatePreview();

    return newGuideline;
  }

  handleAddFromExampleGuidelineEmptyState(guideline: Guideline, example: GuidelineExample) {
    guideline.title = example.title;
    guideline.prompt = example.prompt;
    guideline.createdFromTemplate = true;

    this.updatePreview();
  }

  async createNewGuideline(
    category: GuidelineCategory,
    example: GuidelineExample | null,
  ): Promise<Guideline> {
    let translationCategory = this.convertCategoryToTranslationKey(category);
    let guidelineData: any = {
      category,
      title: example?.title
        ? example.title
        : this.intl.t(
            `ai-agent.guidance.guidelines.categories.${translationCategory}.guideline-title`,
          ),
    };

    if (example?.prompt) {
      guidelineData.prompt = example.prompt;
    }

    let newGuideline = this.store.createRecord('ai-agent/guidelines', guidelineData);

    if (example) {
      newGuideline.createdFromTemplate = true;
    }

    await newGuideline.save();

    return newGuideline;
  }

  updatePreview() {
    this.handleUserIsTyping();
    this.callComposerRefreshHooks();
  }

  registerRefreshHook(hook: () => void) {
    this.composerRefreshHooks.push(hook);
  }

  unregisterRefreshHook(hook: () => void) {
    this.composerRefreshHooks.removeObject(hook);
  }

  sendPreviewSettingsUpdatedMessage() {
    this.sendMessageToMessengerIFrame('preview-settings-updated');
  }

  handleUserIsTyping() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    this.typingTimeout = setTimeout(() => {
      this.sendPreviewSettingsUpdatedMessage();
      this.typingTimeout = null;
    }, PREVIEW_REFRESH_INTERVAL);
  }

  private sendMessageToMessengerIFrame(messageType: PreviewSettingsMessageType) {
    // temporary workaround for tests - skip if app is already torn down
    if (
      !this.appService.app ||
      this.appService.app.isDestroyed ||
      this.appService.app.isDestroying
    ) {
      return;
    }

    let message: {
      type: PreviewSettingsMessageType;
      previewGuidelines?: { text: string; category: string }[];
      previewPersonalitySettings?: Settings;
    } = {
      type: messageType,
      previewGuidelines: this.guidelines
        .filter((guideline: Guideline) => !guideline.isDeleted && guideline.category !== 'copilot')
        .map((guideline: Guideline) => {
          return {
            text: guideline.prompt,
            category: guideline.category,
            title: guideline.title,
            segmentIds: guideline.aiContentSegmentIds,
          };
        }),
      previewPersonalitySettings: this.personalityCustomizationSettings,
    };

    let iframeId = this.getIframeId();
    this.postMessageToIframe(iframeId, JSON.stringify(message));
  }

  getIframeId(): string {
    return this.appService.app.canUseFinGuidanceReusablePreview
      ? '#hosted-messenger-unified-preview'
      : '#hosted-messenger-fin-demo-preview';
  }

  postMessageToIframe(iframeId: string, message: string): void {
    let iframe = window.document.querySelector(iframeId) as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(message);
    }
  }

  setOpenGuidelineId(guidelineId: string | null): void {
    if (this.openGuidelineId === guidelineId) {
      this.openGuidelineId = null;
    } else {
      this.openGuidelineId = guidelineId;
    }
  }

  async saveEscalationPredicates(): Promise<void> {
    // Save to backend
    await this.saveEscalationPredicatesToBackend();
  }

  markEscalationPredicatesAsChanged(): void {
    this.hasUnsavedEscalationChanges = true;
  }

  async loadEscalationPredicates(): Promise<void> {
    this.escalationPredicatesRecord = await this.store.findRecord(
      'ai-agent/escalation-predicates',
      this.appService.app.id,
    );
  }

  async saveEscalationPredicatesToBackend(): Promise<void> {
    try {
      // Simply save the record - the serializer will handle the transformation
      await this.escalationPredicatesRecord?.save();

      // Clear the unsaved changes flag after successful save
      this.hasUnsavedEscalationChanges = false;
    } catch (error) {
      console.error('Failed to save escalation predicates:', error);
      throw error;
    }
  }
}
